import React, { useContext, useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { closePanel } from '@state/actions/panelsActions'
import Button from '@components/Button'
import ButtonWithLoader from '@components/ButtonWithLoader'
import Heading from '@components/Heading'
import GridColumns from '@components/GridColumns'
import Divider from '@components/Divider'
import Form from '@components/Form'
import Text from '@components/Text'
import {
  PANELS,
  DIVIDER_SIZE,
  BUTTON_SIZE,
  HEADING_TYPE,
  BUTTON_TYPE,
  TEXT_SIZES,
} from '@helpers/constants'
import { useCurrentUser } from '@hooks/useCurrentUser'
import { useCreateStudent } from '@hooks/mutations/useCreateStudent'
import { AppContext } from '@state/context'
import { trackRegisterStudent } from '@helpers/matomo'
import withTooltip from '@components/WithTooltip'

const InputWithTooltip = withTooltip(Form.Input)

const CreateStudentPanel = () => {
  const [, dispatch] = useContext(AppContext)
  const { currentUser } = useCurrentUser()
  const { register, handleSubmit, errors, watch, control, formState } = useForm({ mode: 'onChange' })
  const { isValid } = formState
  const schoolValue = watch('school')
  const passwordValue = watch('password')

  const availableSchools = currentUser?.schools?.map((school) => ({
    name: school.name,
    value: school.id,
  }))

  const creatorId =
    currentUser && currentUser.id ? parseInt(currentUser.id) : null

  useEffect(() => {
    trackRegisterStudent('open')
  }, [])
  const { post, isPosting } = useCreateStudent()

  const onSubmit = async (data) => {
    trackRegisterStudent('submit')

    const res = await post({
      input: {
        email: data.email,
        school_id: parseInt(data.school),
        first_name: data.firstName,
        last_name: data.lastName,
        class_ids: data.classes,
        password: data.password,
        password_confirmation: data.password_confirmation,
        creator: creatorId,
      },
    })

    if (res.success) {
      dispatch(closePanel(PANELS.CREATE_STUDENT))
    }
  }

  const schoolClasses = currentUser?.schools
    ?.find((school) => school.id === schoolValue)
    ?.schoolClasses?.map((schoolClass) => ({
      value: schoolClass.id,
      name: schoolClass.name,
    }))

  return (
    <>
      <div className='flex justify-end'>
        <Button
          handleClick={() => {
            dispatch(closePanel(PANELS.CREATE_STUDENT))
            trackRegisterStudent('cancel')
          }}
          size={BUTTON_SIZE.NO_PADDING}
          icon={'Close'}
        ></Button>
      </div>

      <Heading headingLevel={HEADING_TYPE.H2} size={TEXT_SIZES.XLARGE}>
        Skriv in elev
      </Heading>

      <Divider size={DIVIDER_SIZE.BASE} />

      <Form onSubmit={handleSubmit(onSubmit)}>
        <GridColumns cols={2}>
          <div>
            <Form.Label htmlFor='firstName'>Förnamn</Form.Label>
            <Form.Input
              type={'text'}
              name='firstName'
              id='firstName'
              ref={register({ required: true })}
              placeholder='Förnamn'
            />
            {errors.firstName && (
              <Form.ErrorMessage>Förnamn behövs</Form.ErrorMessage>
            )}
          </div>
          <div>
            <Form.Label htmlFor='lastName'>Efternamn</Form.Label>
            <Form.Input
              type={'text'}
              name='lastName'
              id='lastName'
              ref={register({ required: true })}
              placeholder='Efternamn'
            />
            {errors.lastName && (
              <Form.ErrorMessage>Efternamn behövs</Form.ErrorMessage>
            )}
          </div>
        </GridColumns>

        <Divider size={DIVIDER_SIZE.SMALL} />

        <GridColumns cols={2}>
          <div>
            <Form.Label htmlFor='email'>Mejladress</Form.Label>
            <InputWithTooltip
              type={'email'}
              name='email'
              id='email'
              ref={register({ required: true })}
              placeholder='Elevens Mejladress'
              autoComplete='off'
              tooltip={'Det går bra att använda en påhittad adress, t.ex namn@skolansnamn.se'}
            />
            {errors.email && (
              <Form.ErrorMessage>Mejladress behövs</Form.ErrorMessage>
            )}
          </div>
          <div>
            <Form.Label htmlFor='school'>Skola</Form.Label>
            <Controller
              control={control}
              name={'school'}
              defaultValue={null}
              rules={{
                required: true,
              }}
              render={({ onChange, name }) => (
                <Form.MultiSelect
                  placeholder={'Välj skola...'}
                  name={name}
                  onChange={(option) => onChange(option.value)}
                  options={availableSchools}
                  maxWidth={'500px'}
                />
              )}
            />
            {errors.school && (
              <Form.ErrorMessage>Skola behövs</Form.ErrorMessage>
            )}
          </div>
        </GridColumns>

        <Divider size={DIVIDER_SIZE.SMALL} />

        <GridColumns cols={2}>
          <div>
            <Form.Label htmlFor='classes'>Välj klasser</Form.Label>
            <Controller
              control={control}
              defaultValue={null}
              name={'classes'}
              rules={{ required: true }}
              render={({ onChange, name }) => (
                <Form.MultiSelect
                  placeholder={
                    schoolClasses ? 'Välj klasser...' : 'Välj skola först'
                  }
                  name={name}
                  onChange={(selectedOptions) => {
                    onChange(
                      selectedOptions.map((option) => parseInt(option.value))
                    )
                  }}
                  options={schoolClasses}
                  isDisabled={!schoolClasses}
                  maxWidth={'500px'}
                  hideSelectedOptions={true}
                  isMulti
                />
              )}
            />
            {errors.classes && <Form.ErrorMessage />}
          </div>
        </GridColumns>

        <Divider size={DIVIDER_SIZE.SMALL} />

        <GridColumns cols={2}>
          <div>
            <Form.Label htmlFor='lastName'>Lösenord</Form.Label>
            <Form.Input
              type={'password'}
              name='password'
              id='password'
              ref={register({
                required: 'Lösenord behövs',
                minLength: {
                  value: 8,
                  message: 'lösenordet måste vara minst 8 tecken långt',
                },
                maxLength: {
                  value: 64,
                  message: 'lösenordet får vara som mest 64 tecken långt',
                },
              })}
              placeholder='Lösenord'
              autoComplete='new-password'
            />
            {errors.password && (
              <Form.ErrorMessage>{errors.password.message}</Form.ErrorMessage>
            )}
          </div>
          <div>
            <Form.Label htmlFor='password_confirmation'>
              Upprepa lösenord
            </Form.Label>
            <Form.Input
              type={'password'}
              name='password_confirmation'
              id='password_confirmation'
              ref={register({
                required: 'Fyll i lösenordet igen',
                validate: {
                  passwordsMatch: (value) =>
                    passwordValue === value || 'Lösenorden är inte samma',
                },
              })}
              placeholder='Upprepa lösenord'
              autoComplete='new-password'
            />
            {errors?.password_confirmation && (
              <Form.ErrorMessage>
                {errors.password_confirmation.message}
              </Form.ErrorMessage>
            )}
          </div>
        </GridColumns>

        <Divider size={DIVIDER_SIZE.BASE} />

        <ButtonWithLoader
          type={BUTTON_TYPE.PRIMARY}
          isLoading={isPosting}
          enabledText={'Skriv in elev'}
          disabled={!isValid || isPosting}
        />
      </Form>

      <Divider size={DIVIDER_SIZE.SMALL} />

      <Text bold>Bra att veta</Text>
      <Text>
        &bull; Eleven loggar in på Doklet (
        <a
          className={'underline text-primaryGreen'}
          href='https://beta.doklet.se/login'
          target='_blank'
        >
          https://beta.doklet.se/login
        </a>
        ) med mejladress och sitt lösenord.
        <br />
        &bull; Lärarkollegor på samma skola ser varandras elever och kan lägga
        in dem i grupper.
      </Text>
    </>
  )
}

export default CreateStudentPanel
